import { useMemo, useState } from 'react';
import { StickyNote } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button, Chip } from '@vnetwork/ui/primitives';
import { EmptyState, Timeline } from '@vnetwork/ui/data';
import type { TimelineItem } from '@vnetwork/ui/data';
import { AccountAvatar } from '@/pages/account/components/AccountAvatar';
import {
  useJourneyNotes,
  type CustomerJourney,
  type JourneyNote,
} from '@/api/customer-journey';
import { TabError } from '@/pages/customers/detail/tab-states';
import { stepLabel } from './sale-process-helpers';
import { noteTime, noteAuthor, timeLabel, mergeNotes, groupNotesByDate } from './journey-notes-utils';
import { AddJourneyNoteDialog } from './add-journey-note-dialog';

/**
 * Notes timeline for a journey. Prefers the notes embedded on the journey row
 * (`customer_journey_notes`) and falls back to the standalone notes endpoint,
 * de-duped and sorted newest-first. Each entry shows the step Chip, note text,
 * author and datetime — rendered via @ui Timeline. "Add note" opens a dialog
 * with a step select.
 */

function noteToTimelineItem(note: JourneyNote, index: number): TimelineItem {
  const actor = noteAuthor(note);
  return {
    id: note.customer_journey_note_id ?? index,
    title: (
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <AccountAvatar name={actor} size={24} />
        <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-sm)' }}>
          {actor}
        </span>
        {note.customer_journey_note_step ? (
          <Chip tone="neutral" appearance="subtle" size="sm">
            {stepLabel(note.customer_journey_note_step)}
          </Chip>
        ) : null}
      </span>
    ),
    timestamp: (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-subtle)', fontVariantNumeric: 'tabular-nums' }}>
        {timeLabel(noteTime(note))}
      </span>
    ),
    description: note.customer_journey_note ? (
      <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {note.customer_journey_note}
      </p>
    ) : undefined,
    tone: 'neutral',
  };
}

export function JourneyNotesTimeline({
  customerId,
  journey,
}: {
  customerId: number;
  journey: CustomerJourney;
}) {
  const [adding, setAdding] = useState(false);
  const journeyId = journey.customer_journey_id;
  const query = useJourneyNotes(customerId, journeyId);
  const fetched = query.data?.results ?? [];
  const embedded = journey.customer_journey_notes ?? [];

  const groups = useMemo(() => groupNotesByDate(mergeNotes(embedded, fetched)), [embedded, fetched]);
  const total = groups.reduce((n, g) => n + g.notes.length, 0);

  return (
    <Card flat>
      <CardHeader
        actions={
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            Add note
          </Button>
        }
      >
        <CardTitle>Notes{total ? ` · ${total}` : ''}</CardTitle>
      </CardHeader>
      <CardBody>
        {query.isError && total === 0 ? (
          <TabError title="Couldn't load notes" onRetry={() => query.refetch()} />
        ) : total === 0 ? (
          <EmptyState icon={<StickyNote size={28} />} title="No notes yet" description="Log follow-ups and call notes here." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-5)' }}>
            {groups.map((group) => (
              <section key={group.key}>
                <div style={{ marginBottom: 'var(--vn-space-3)' }}>
                  <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-sm)' }}>
                    {group.label}
                  </span>
                </div>
                <Timeline
                  items={group.notes.map((n, i) => noteToTimelineItem(n, i))}
                  size="sm"
                />
              </section>
            ))}
          </div>
        )}
      </CardBody>

      <AddJourneyNoteDialog
        customerId={customerId}
        journey={journey}
        open={adding}
        onOpenChange={setAdding}
      />
    </Card>
  );
}

export default JourneyNotesTimeline;
